import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Search, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { API } from '@/lib/api';



const CATEGORIES = [
  { key: 'all', de: 'Alle', en: 'All' },
  { key: 'ernährung', de: 'Ernährung', en: 'Nutrition' },
  { key: 'ernaehrung', de: 'Ernährung', en: 'Nutrition' },
  { key: 'lifestyle', de: 'Lifestyle & Glow', en: 'Lifestyle & Glow' },
  { key: 'life', de: 'belosophie LIFE', en: 'belosophie LIFE' },
];

export default function BlogPage() {
  const { language } = useLanguage();
  const nav = useNavigate();
  const de = language === 'de';
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const params = {};
        if (category !== 'all') params.category = category;
        const res = await axios.get(`${API}/content`, { params });
        setArticles(res.data || []);
      } catch { setArticles([]); }
    };
    fetchArticles();
  }, [category]);

  const filtered = articles.filter(a => {
    const title = de ? (a.title_de || a.title) : (a.title_en || a.title);
    return search === '' || (title || '').toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="min-h-screen bg-bone" data-testid="blog-list-page">
      <div className="max-w-5xl mx-auto px-6 py-12 md:py-20">
        <button onClick={() => nav('/')} className="flex items-center gap-2 text-foreground/60 hover:text-foreground mb-8 font-body text-sm transition-colors" data-testid="blog-back-btn">
          <ArrowLeft size={16} /> <span className="font-display text-xl font-semibold"><span className="text-terracotta">bel</span>osophie</span>
        </button>
        <div className="text-center mb-12">
          <p className="font-body text-sm uppercase tracking-[0.2em] text-terracotta font-semibold mb-3">BLOG</p>
          <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight">
            {de ? 'Wissen & Inspiration' : 'Knowledge & Inspiration'}
          </h1>
          <p className="font-body text-base md:text-lg text-foreground/50 mt-4 max-w-2xl mx-auto">
            {de ? 'Expertenwissen zu Ernährung, Sport, Gesundheit und Lifestyle für Frauen 40+' : 'Expert knowledge on nutrition, sports, health and lifestyle for women 40+'}
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/30" />
            <Input
              placeholder={de ? 'Blog durchsuchen...' : 'Search blog...'}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10 rounded-full border-border/80"
              data-testid="blog-search"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map(c => (
              <button
                key={c.key}
                onClick={() => setCategory(c.key)}
                className={`px-4 py-2 rounded-full font-body text-sm font-medium transition-all border ${category === c.key ? 'bg-terracotta text-white border-terracotta' : 'bg-white text-foreground/60 border-border/60 hover:border-terracotta/50'}`}
                data-testid={`blog-cat-${c.key}`}
              >
                {de ? c.de : c.en}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
          {filtered.map((article, i) => {
            const title = de ? (article.title_de || article.title) : (article.title_en || article.title);
            const desc = de ? (article.description_de || article.content_de) : (article.description_en || article.content_en);
            return (
              <Card
                key={article.id || i}
                className="bg-white border border-border/60 rounded-2xl overflow-hidden hover:shadow-md transition-all cursor-pointer"
                onClick={() => nav(`/content`)}
                data-testid={`blog-article-${i}`}
              >
                {article.image_url && (
                  <img src={article.image_url} alt={title} className="w-full h-40 object-cover" />
                )}
                <div className="p-5">
                  <p className="font-body text-xs text-terracotta font-semibold uppercase mb-2">{article.category || 'Artikel'}</p>
                  <h3 className="font-display text-lg font-medium mb-2">{title}</h3>
                  <p className="font-body text-sm text-foreground/50 line-clamp-3">{desc}</p>
                </div>
              </Card>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16">
            <p className="font-body text-foreground/40">{de ? 'Keine Artikel gefunden' : 'No articles found'}</p>
          </div>
        )}
      </div>
    </div>
  );
}
